import { useEffect, useState } from "react";

import type { ShopifyImportToastPhase } from "./sections/ShopifyImportToast";

const IMPORT_STEP_MS = 400;
const SLOW_IMPORT_STEP_MS = 1200;

export type CompletedImportPayload = {
  productIds: string[];
  autoPublish: boolean;
};

type ShopifyImportSessionState = {
  visible: boolean;
  phase: ShopifyImportToastPhase;
  completed: number;
  total: number;
  autoPublish: boolean;
  importProductIds: string[];
  completedImport: CompletedImportPayload | null;
};

const initialState: ShopifyImportSessionState = {
  visible: false,
  phase: "importing",
  completed: 0,
  total: 0,
  autoPublish: true,
  importProductIds: [],
  completedImport: null,
};

let sessionState: ShopifyImportSessionState = initialState;
let importTimer: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();

const notify = (): void => {
  listeners.forEach((listener) => listener());
};

const setSession = (
  patch: Partial<ShopifyImportSessionState> | ShopifyImportSessionState
): void => {
  sessionState = { ...sessionState, ...patch };
  notify();
};

const clearImportTimer = (): void => {
  if (importTimer) {
    clearInterval(importTimer);
    importTimer = null;
  }
};

export const getShopifyImportSession = (): ShopifyImportSessionState =>
  sessionState;

export const subscribeShopifyImportSession = (
  listener: () => void
): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useShopifyImportSession = (): ShopifyImportSessionState => {
  const [, bump] = useState(0);
  useEffect(
    () =>
      subscribeShopifyImportSession(() => {
        bump((n) => n + 1);
      }),
    []
  );
  return sessionState;
};

export type StartShopifyImportOptions = {
  productIds: string[];
  autoPublish: boolean;
  simulateSlowImport?: boolean;
  simulateImportError?: boolean;
};

export const startShopifyImportSession = ({
  productIds,
  autoPublish,
  simulateSlowImport = false,
  simulateImportError = false,
}: StartShopifyImportOptions): void => {
  clearImportTimer();

  setSession({
    visible: true,
    phase: "importing",
    completed: 0,
    total: productIds.length,
    autoPublish,
    importProductIds: productIds,
    completedImport: null,
  });

  const stepMs = simulateSlowImport ? SLOW_IMPORT_STEP_MS : IMPORT_STEP_MS;

  if (simulateImportError) {
    importTimer = setInterval(() => {
      const { completed, total } = sessionState;
      const next = completed + 1;
      if (next >= total) {
        clearImportTimer();
        setSession({ completed: total, phase: "error" });
        return;
      }
      setSession({ completed: next });
    }, stepMs);
    return;
  }

  importTimer = setInterval(() => {
    const {
      completed,
      total,
      importProductIds: ids,
      autoPublish: publish,
    } = sessionState;
    const next = completed + 1;
    if (next >= total) {
      clearImportTimer();
      setSession({
        completed: next,
        phase: "success",
        completedImport: { productIds: ids, autoPublish: publish },
      });
      return;
    }
    setSession({ completed: next });
  }, stepMs);
};

export const dismissShopifyImportSession = (): void => {
  clearImportTimer();
  setSession({
    visible: false,
    phase: "importing",
    completed: 0,
    total: 0,
    importProductIds: [],
  });
};

export const consumeCompletedImportSession =
  (): CompletedImportPayload | null => {
    const payload = sessionState.completedImport;
    if (!payload) return null;
    setSession({ completedImport: null });
    return payload;
  };
